import { APP_INITIALIZER, Injector, Provider } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { AnalyticsGateway } from '@domain/models/analytics/gateway/analytics.gateway';
import { AuthService } from '@infraestructure/drive-adapter/auth/auth.service';
import { isGa4TrackedPortalUrl } from './ga4-url.helper';

function deepestRouteTitle(route: ActivatedRouteSnapshot | null): string | undefined {
  let current = route;
  while (current?.firstChild) current = current.firstChild;
  const title = current?.data?.['title'];
  return typeof title === 'string' ? title : undefined;
}

export function startAnalyticsPageViews(router: Router, injector: Injector, analytics: AnalyticsGateway): void {
  let wasOnPortalPage = false;

  router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
    try {
      const url = event.urlAfterRedirects;
      if (!isGa4TrackedPortalUrl(url)) {
        // Leaving the portal (sign-out, idle timeout, expired session) ends collection
        if (wasOnPortalPage) analytics.reset();
        wasOnPortalPage = false;
        return;
      }

      wasOnPortalPage = true;
      // Resolved lazily: AuthService's constructor touches storage and must not run at boot
      const authService = injector.get(AuthService);
      if (!authService.isAuthenticated()) return;

      const title = deepestRouteTitle(router.routerState.snapshot.root);
      authService
        .getUser()
        .pipe(take(1))
        .subscribe((member) => {
          // RxJS reports errors thrown in next() asynchronously, so the outer try can't catch them
          try {
            analytics.trackPageView(url, title, member);
          } catch {
            // analytics must never break navigation
          }
        });
    } catch {
      // analytics must never break navigation
    }
  });
}

/** One manual page_view per completed post-login navigation, including the first load. */
export function provideAnalyticsPageViews(): Provider {
  return {
    provide: APP_INITIALIZER,
    multi: true,
    deps: [Router, Injector, AnalyticsGateway],
    useFactory: (router: Router, injector: Injector, analytics: AnalyticsGateway) => () => startAnalyticsPageViews(router, injector, analytics),
  };
}
